//app/frontend/components/AddressAutocompleteMap.tsx
import React, { useRef, useEffect, useState } from 'react';
import { TextInput, Select, Text } from '@mantine/core';
import { Autocomplete } from '@react-google-maps/api';

interface TimezoneOption {
  value: string;
  label: string;
}

interface Area {
  id: number;
  name: string;
  country_id: number;
}

interface City {
  id: number;
  name: string;
  area_id: number;
  area_name: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onTimezoneSelect: (timeZoneId: string) => void;
  timezoneValue: string;
  onCitySelect?: (cityData: CityData | null) => void;
  cityValue?: CityData | null;
  areas?: Area[];
  cities?: City[];
}

interface CityData {
  cityName: string;
  countryName: string;
  countryCode: string;
  stateName: string;
  continentName: string;
  cityFound: boolean;
  missingCityName: string | null;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onTimezoneSelect,
  timezoneValue,
  onCitySelect,
  cityValue,
  areas,
  cities,
}) => {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [timezoneOptions, setTimezoneOptions] = useState<TimezoneOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [detectedLocation, setDetectedLocation] = useState<{
    city: string;
    area: string;
    country: string;
    continent: string;
  } | null>(null);

  // Fetch list of timezones on component mount
  useEffect(() => {
    fetchTimezoneList();
  }, []);

  const findAreaFromCities = (cityName: string, stateName: string) => {
    if (!cities || !cityName) {
      return {
        areaName: stateName || 'Area not found',
        cityFound: false,
      };
    }

    // Look for the city in the cities array
    const foundCity = cities.find((city) => city.name.toLowerCase() === cityName.toLowerCase());

    if (foundCity) {
      return {
        areaName: foundCity.area_name,
        cityFound: true,
      };
    } else {
      // City not found - we'll notify backend to alert admin
      return {
        areaName: stateName || 'Area not found',
        cityFound: false,
      };
    }
  };

  const extractLocationData = (place: google.maps.places.PlaceResult) => {
    const components = place.address_components || [];

    let cityName = '';
    let countryName = '';
    let countryCode = '';
    let stateName = '';
    let continentName = '';

    components.forEach((component) => {
      const types = component.types;

      // Extract city name (locality or administrative_area_level_1 for some places)
      if (types.includes('locality')) {
        cityName = component.long_name;
      } else if (types.includes('administrative_area_level_1') && !cityName) {
        cityName = component.long_name;
      }

      // Extract state/province (administrative_area_level_1)
      if (types.includes('administrative_area_level_1')) {
        stateName = component.long_name;
      }

      // Extract country info
      if (types.includes('country')) {
        countryName = component.long_name;
        countryCode = component.short_name; // This gives us the ISO code like 'US', 'CA'
      }
    });

    // Map country codes to continents (basic mapping)
    const continentMap: { [key: string]: string } = {
      US: 'North America',
      CA: 'North America',
      MX: 'North America',
      GB: 'Europe',
      FR: 'Europe',
      DE: 'Europe',
      IT: 'Europe',
      ES: 'Europe',
      AU: 'Oceania',
      NZ: 'Oceania',
      JP: 'Asia',
      CN: 'Asia',
      IN: 'Asia',
      BR: 'South America',
      AR: 'South America',
      ZA: 'Africa',
      NG: 'Africa',
      EG: 'Africa',
      // Add more as needed
    };

    continentName = continentMap[countryCode] || 'Unknown';

    return { cityName, countryName, countryCode, stateName, continentName };
  };

  const fetchAreaFromDatabase = async (cityName: string, countryCode: string) => {
    try {
      const response = await fetch(
        `/cities/lookup_area?city_name=${encodeURIComponent(cityName)}&country_code=${countryCode}`,
        {
          headers: {
            Accept: 'application/json',
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        return data.area_name || 'Area not found';
      } else {
        console.error('Failed to fetch area from database:', response.status);
        return 'Area lookup failed';
      }
    } catch (error) {
      console.error('Error fetching area:', error);
      return 'Area lookup error';
    }
  };

  const findOrCreateCity = async (cityName: string, countryCode: string) => {
    if (!cityName || !countryCode) {
      console.warn('Missing city name or country code');
      return null;
    }

    try {
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;

      const response = await fetch('/api/cities/find_or_create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
        body: JSON.stringify({
          city_name: cityName,
          country_iso_code: countryCode,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('City found/created:', data);
        return data.city_id;
      } else {
        console.error('Failed to find/create city:', response.status);
        return null;
      }
    } catch (error) {
      console.error('Error finding/creating city:', error);
      return null;
    }
  };

  const fetchTimezoneList = async () => {
    try {
      const apiKey = 'N2IE9BKU2EQR'; // Your TimeZoneDB API key
      const response = await fetch(`https://api.timezonedb.com/v2.1/list-time-zone?key=${apiKey}&format=json`);
      const data = await response.json();

      if (data.status === 'OK' && data.zones) {
        const options = data.zones.map((zone: any) => ({
          value: zone.zoneName,
          label: `${zone.zoneName} (${formatGMTOffset(zone.gmtOffset)})`,
        }));
        setTimezoneOptions(options);
      } else {
        console.error('Error fetching timezone list:', data.message);
      }
    } catch (error) {
      console.error('Error fetching timezone list:', error);
    }
  };

  const formatGMTOffset = (offsetSeconds: number) => {
    const hours = Math.floor(Math.abs(offsetSeconds) / 3600);
    const minutes = Math.floor((Math.abs(offsetSeconds) % 3600) / 60);
    const sign = offsetSeconds >= 0 ? '+' : '-';
    return `GMT${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const handleLoad = (autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      console.log('🛐 place:', place);
      if (place && place.formatted_address && place.geometry) {
        const address = place.formatted_address;
        onChange(address);

        // Extract enhanced location data
        const { cityName, countryName, countryCode, stateName, continentName } = extractLocationData(place);
        console.log('🛐 Extracted location:', { cityName, countryName, countryCode, stateName, continentName });

        const { areaName, cityFound } = findAreaFromCities(cityName, stateName);

        console.log('🛐 Area from cities:', areaName, 'City found:', cityFound);

        // Set the detected location for display
        setDetectedLocation({
          city: cityName || 'Not detected',
          area: areaName,
          country: countryName || 'Not detected',
          continent: continentName || 'Not detected',
        });

        // Pass enhanced city data to parent
        if (onCitySelect && cityName && countryCode) {
          onCitySelect({
            cityName,
            countryName,
            countryCode,
            stateName,
            continentName,
            cityFound,
            missingCityName: !cityFound ? cityName : null,
          });
        }

        const location = place.geometry?.location;
        if (location) {
          fetchTimezoneAndUpdateField(location.lat(), location.lng());
        }
      }
    }
  };

  const fetchTimezoneAndUpdateField = async (lat: number, lng: number) => {
    setLoading(true);
    console.log('🛐 Fetching timezone for:', lat, lng);
    try {
      const apiKey = 'N2IE9BKU2EQR'; // Your TimeZoneDB API key
      const url = `https://api.timezonedb.com/v2.1/get-time-zone?key=${apiKey}&format=json&by=position&lat=${lat}&lng=${lng}`;

      const response = await fetch(url);
      const data = await response.json();

      console.log('🛐 data:', data);
      console.log('🛐 data.status:', data.status);

      if (data.status === 'OK') {
        console.log('Fetched Timezone:', data.zoneName);
        // Just pass the raw timezone ID directly
        onTimezoneSelect(data.zoneName);
      } else {
        console.error('Error in TimeZoneDB data:', data.message);
      }
    } catch (error) {
      console.error('Error fetching timezone:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Autocomplete onLoad={handleLoad} onPlaceChanged={handlePlaceChanged}>
        <TextInput
          label="Address"
          name="Address"
          placeholder="Event Address"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          labelProps={{
            style: {
              display: 'block',
              textAlign: 'left',
              marginBottom: '5px',
              color: 'white',
            },
          }}
          styles={{ label: { color: '#FFF7ED' } }}
        />
      </Autocomplete>

      <Select
        label="Timezone"
        placeholder="Select timezone"
        name="Timezone"
        data={timezoneOptions}
        value={timezoneValue}
        onChange={(value) => value && onTimezoneSelect(value)}
        searchable
        labelProps={{
          style: {
            display: 'block',
            textAlign: 'left',
            marginBottom: '5px',
            color: 'white',
          },
        }}
        disabled={loading}
        styles={{ label: { color: '#FFF7ED' } }}
      />

      {detectedLocation && (
        <div
          style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#2d2d2d',
            borderRadius: '8px',
            border: '1px solid #404040',
          }}
        >
          <Text size="sm" fw={500} style={{ color: '#FFF7ED', marginBottom: '8px' }}>
            Detected Location:
          </Text>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
            <div>
              <Text size="xs" style={{ color: '#888', marginBottom: '2px' }}>
                City:
              </Text>
              <Text size="sm" style={{ color: '#FFF7ED' }}>
                {detectedLocation.city}
              </Text>
            </div>
            <div>
              <Text size="xs" style={{ color: '#888', marginBottom: '2px' }}>
                Country:
              </Text>
              <Text size="sm" style={{ color: '#FFF7ED' }}>
                {detectedLocation.country}
              </Text>
            </div>
            <div>
              <Text size="xs" style={{ color: '#888', marginBottom: '2px' }}>
                Area:
              </Text>
              <Text size="sm" style={{ color: '#FFF7ED' }}>
                {detectedLocation.area}
              </Text>
            </div>
            <div>
              <Text size="xs" style={{ color: '#888', marginBottom: '2px' }}>
                Continent:
              </Text>
              <Text size="sm" style={{ color: '#FFF7ED' }}>
                {detectedLocation.continent}
              </Text>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddressAutocomplete;
